import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/components/Seo";
import { DemoBanner } from "@/components/portal/DemoBanner";
import {
  getIntegrationStatus,
  integrations as integrationDefaults,
  type IntegrationInfo,
  type IntegrationMode,
} from "@/features/integrations/status";
import { listApplications } from "@/features/applications/repo";
import { listContactMessages } from "@/features/contact/repo";
import { listUserProfiles } from "@/features/users/repo";
import { listDocuments } from "@/features/documents/repo";
import { listActiveLoads } from "@/features/loads/repo";
import { countFleetNeedingAttention } from "@/features/fleet/repo";
import { useAuth } from "@/features/auth/useAuth";
import { canViewOperations } from "@/features/auth/types";

const modeBadge: Record<IntegrationMode, { label: string; variant: "default" | "success" | "warning" }> = {
  not_configured: { label: "not configured", variant: "warning" },
  read_only: { label: "read-only", variant: "default" },
  read_write: { label: "read/write", variant: "success" },
};

export default function InternalDashboard() {
  const { user } = useAuth();
  const showOps = canViewOperations(user);
  const [activeLoads, setActiveLoads] = useState(0);
  const [fleetAlerts, setFleetAlerts] = useState(0);
  const [newApplications, setNewApplications] = useState(0);
  const [newMessages, setNewMessages] = useState(0);
  const [userProfiles, setUserProfiles] = useState(0);
  const [newDocuments, setNewDocuments] = useState(0);
  const [integrations, setIntegrations] = useState<IntegrationInfo[]>(integrationDefaults);

  useEffect(() => {
    getIntegrationStatus()
      .then(setIntegrations)
      .catch(() => setIntegrations(integrationDefaults));
  }, []);

  useEffect(() => {
    if (showOps) {
      listActiveLoads(user)
        .then((loads) => setActiveLoads(loads.length))
        .catch(() => setActiveLoads(0));
      countFleetNeedingAttention()
        .then(setFleetAlerts)
        .catch(() => setFleetAlerts(0));
    }
    listApplications()
      .then((apps) => setNewApplications(apps.filter((a) => a.status === "new").length))
      .catch(() => setNewApplications(0));
    listContactMessages()
      .then((messages) => setNewMessages(messages.filter((m) => m.status === "new").length))
      .catch(() => setNewMessages(0));
    listUserProfiles()
      .then((users) => setUserProfiles(users.length))
      .catch(() => setUserProfiles(0));
    listDocuments()
      .then((documents) => setNewDocuments(documents.filter((doc) => doc.status === "new").length))
      .catch(() => setNewDocuments(0));
  }, [user, showOps]);

  return (
    <>
      <Seo title="Internal dashboard" noindex />
      <DemoBanner />
      <div>
        <h1 className="text-2xl font-bold">Operations overview</h1>
        <p className="mt-1 max-w-3xl text-sm text-brand-500">
          Start here for daily exceptions. Cards point to the staff pages where each
          item can be reviewed or updated.
        </p>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {showOps && <Stat label="Active loads" value={activeLoads} to="/app/internal/loads" />}
        {showOps && <Stat label="Fleet alerts" value={fleetAlerts} to="/app/internal/fleet" tone={fleetAlerts > 0 ? "warning" : "default"} />}
        <Stat label="New applications" value={newApplications} to="/app/internal/applications" tone={newApplications > 0 ? "warning" : "default"} />
        <Stat label="New messages" value={newMessages} to="/app/internal/messages" tone={newMessages > 0 ? "warning" : "default"} />
        <Stat label="New documents" value={newDocuments} to="/app/internal/documents" tone={newDocuments > 0 ? "warning" : "default"} />
        <Stat label="User profiles" value={userProfiles} to="/app/internal/users" />
        <Stat label="Integrations live" value={integrations.filter((i) => i.mode !== "not_configured").length} />
      </div>

      <div className="mt-8 rounded-xl border border-brand-100 bg-white p-5 shadow-sm">
        <h2 className="text-base font-semibold text-brand-900">Suggested next checks</h2>
        <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <ActionLink to="/app/internal/applications" label="Review new applications" count={newApplications} />
          <ActionLink to="/app/internal/messages" label="Answer contact messages" count={newMessages} />
          <ActionLink to="/app/internal/documents" label="Review new documents" count={newDocuments} />
          {showOps && <ActionLink to="/app/internal/fleet" label="Check fleet alerts" count={fleetAlerts} />}
          {showOps && <ActionLink to="/app/internal/loads" label="Review active loads" count={activeLoads} />}
          <ActionLink to="/app/internal/activity" label="Audit recent activity" />
        </div>
      </div>

      <h2 className="mt-10 text-lg font-semibold">Integration status</h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {integrations.map((i) => {
          const b = modeBadge[i.mode];
          return (
            <Card key={i.key}>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CardTitle className="break-words">{i.name}</CardTitle>
                  <Badge variant={b.variant}>{b.label}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="break-words text-sm text-brand-600">{i.purpose}</p>
                {i.note && <p className="mt-1 break-words text-xs text-brand-400">{i.note}</p>}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
}

function Stat({
  label,
  value,
  to,
  tone = "default",
}: {
  label: string;
  value: number;
  to?: string;
  tone?: "default" | "warning";
}) {
  const content = (
    <Card className={tone === "warning" ? "border-amber-200 bg-amber-50" : ""}>
      <CardContent className="pt-6">
        <div className="text-3xl font-bold text-brand-800">{value}</div>
        <div className="mt-1 text-sm text-brand-500">{label}</div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} className="block min-w-0 transition hover:-translate-y-0.5 hover:shadow-sm">{content}</Link> : content;
}

function ActionLink({ to, label, count }: { to: string; label: string; count?: number }) {
  return (
    <Link
      to={to}
      className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-brand-100 px-3 py-2 text-brand-700 transition hover:border-accent-300 hover:bg-accent-500/5"
    >
      <span className="min-w-0 break-words">{label}</span>
      {count !== undefined && (
        <Badge variant={count > 0 ? "warning" : "default"}>{count}</Badge>
      )}
    </Link>
  );
}
